import gql from 'graphql-tag';
import { useEffect, useState } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import { Flags } from '@watershed/constants/flags';
import { WatershedPlanLegacy } from '@watershed/constants/organization';
import { getCurrencyForOrg } from '@watershed/shared-universal/utils/currencies';
import {
  watershedPlanIncludesMeasurement,
  watershedPlanIncludesReductions,
} from '@watershed/shared-universal/utils/watershedPlanUtils';

import { GQActiveOrganizationFieldsFragment } from '../generated/graphql-operations';
import type { FeatureFlagsMap } from './FeatureFlag';
import type { UserContextProps } from './UserContext';

// This giant list of stuff seems suspicious,
// as if pieces belong closer to where they are used…
gql`
  query ActiveOrganization @withOwner(owner: EnterpriseFoundations) {
    activeOrganization {
      ...ActiveOrganizationFields
    }
    flags {
      ...FlagsInitFields
    }
    preferredLocale
  }

  query ForceRedirect($pathname: String)
  @withOwner(owner: EnterpriseFoundations) {
    forceRedirect(pathname: $pathname)
  }

  # This minimal query is used simply to check whether or not the user
  # is authenticated: the request will succeed it they are, get a 401
  # error they are not.
  query CheckUserAuthentication @withOwner(owner: EnterpriseFoundations) {
    activeOrganization {
      id
    }
  }

  fragment UserPermissionFields on UserPermission {
    id
    permission
    objectId
    objectType
  }

  fragment PermissionDetails on PermissionItem {
    id
    permission
    object {
      __typename
      id
    }
  }

  # Be careful about adding new fields to this fragment or child-fragments: this is a blocking query on every page load.
  fragment ActiveOrganizationFields on ActiveOrganization {
    # Simple things on the org model or context
    id
    name
    helpChannel
    demoOrg
    testOrg
    stagingOrg
    region
    watershedPlan
    watershedPlanLegacy
    canAccessFinance
    canAccessCorporate
    fiscalYearStartMonth
    currency
    country
    companyId
    domains
    logoUrl
    iconUrl
    sessionId
    sessionTimeoutMinutes

    # User and logged in user (one step more complicated)
    user {
      id
      createdAt
      name
      accessibleOrgs {
        id
        name
        canAccessInCurrentSession
        companyId
        demoOrg
        testOrg
        stagingOrg
      }
      email
      isWatershedEmployee
      isWatershedContractor
      isE2ETester
    }
    loginAsUser {
      id
      name
      isWatershedEmployee
      isWatershedContractor
      isE2ETester
    }

    # Used all over the place. Onboarding could use more ownership? :shrug:
    userOnboardingsCompleted

    userPermissionsV2 {
      # synthetic id to avoid GQL warnings
      id
      ...UserPermissionFields
    }
  }

  fragment FlagsInitFields on FeatureFlag {
    id
    name
    description
    team
    featureTags
    activated
  }

  mutation ThrowError @withOwner(owner: EnterpriseFoundations) {
    throwError(input: { fooString: "abcd", password: "1234" }) {
      message
    }
  }
`;

function parseUserContextData(
  activeOrg: GQActiveOrganizationFieldsFragment,
  flags: FeatureFlagsMap,
  preferredLocale: string | null
): UserContextProps {
  const user = activeOrg.user;

  // Is this a real customer session?
  const isCustomer =
    !activeOrg.demoOrg &&
    !activeOrg.testOrg &&
    !activeOrg.loginAsUser &&
    !user.isWatershedEmployee &&
    !user.isWatershedContractor &&
    !user.isE2ETester;

  // TODO (reorganize all this code in a sane way)
  datadogLogs.setLoggerGlobalContext({
    orgId: activeOrg.id,
    orgName: activeOrg.name,
    demoOrg: activeOrg.demoOrg,
    testOrg: activeOrg.testOrg,
    stagingOrg: activeOrg.stagingOrg,
    watershedPlanLegacy: activeOrg.watershedPlanLegacy,
    userId: user.id,
    userName: user.name,
    loginAsUserName: activeOrg.loginAsUser?.name,
    isCustomer,
  });

  const currencyCode = getCurrencyForOrg(activeOrg);

  return {
    orgId: activeOrg.id,
    orgName: activeOrg.name,
    orgHelpChannel: activeOrg.helpChannel,
    orgCountry: activeOrg.country,
    demoOrg: activeOrg.demoOrg,
    testOrg: activeOrg.testOrg,
    stagingOrg: activeOrg.stagingOrg,
    region: activeOrg.region,
    watershedPlan: activeOrg.watershedPlan,
    watershedPlanLegacy: activeOrg.watershedPlanLegacy,
    watershedPlanIncludesMeasurement: watershedPlanIncludesMeasurement({
      watershedPlan: activeOrg.watershedPlan,
      watershedPlanLegacy: activeOrg.watershedPlanLegacy,
    }),
    watershedPlanIncludesReductions: watershedPlanIncludesReductions({
      watershedPlan: activeOrg.watershedPlan,
      watershedPlanLegacy: activeOrg.watershedPlanLegacy,
      overrideFlag: flags.get(Flags.PricingFy25ReductionsModuleTemp),
    }),
    watershedPlanIncludesReporting:
      !!flags.get(Flags.PricingFy25ReportingModuleTemp) ||
      !!flags.get(Flags.PricingFy25CsrdReportBuilderModuleTemp),
    watershedPlanIncludesDatasetCadences:
      activeOrg.watershedPlanLegacy === WatershedPlanLegacy.Pro ||
      activeOrg.watershedPlanLegacy === WatershedPlanLegacy.NetZero,
    userId: user.id,
    createdAt: user.createdAt,
    accessibleOrgs: user.accessibleOrgs,
    userName: user.name,
    userEmail: user.email,
    userIsWatershedEmployee: user.isWatershedEmployee,
    userIsWatershedContractor: user.isWatershedContractor,
    userIsE2ETester: user.isE2ETester,
    permissions: activeOrg.userPermissionsV2,
    userOnboardingsCompleted: activeOrg.userOnboardingsCompleted,
    logoUrl: activeOrg.logoUrl,
    iconUrl: activeOrg.iconUrl,
    loginAsUser: activeOrg.loginAsUser,
    isCustomer,
    hasInitialized: true,
    orgFiscalYearStartMonth: activeOrg.fiscalYearStartMonth,
    canAccessCorporate: activeOrg.canAccessCorporate,
    canAccessFinance: activeOrg.canAccessFinance,
    currency: currencyCode,
    companyId: activeOrg.companyId,
    domains: activeOrg.domains,
    sessionId: activeOrg.sessionId,
    sessionTimeoutMinutes: activeOrg.sessionTimeoutMinutes,
    preferredLocale,
  };
}

export default function useParsedUserContext(
  activeOrganization: GQActiveOrganizationFieldsFragment,
  flags: FeatureFlagsMap,
  preferredLocale: string | null
): UserContextProps {
  const [parsedContext, setParsedContext] = useState(() =>
    parseUserContextData(activeOrganization, flags, preferredLocale)
  );

  //  data in the context has changed (including overrides).
  // If it has, we'll generate a new context so the app re-renders itself correctly.
  const newParsedContextData = parseUserContextData(
    activeOrganization,
    flags,
    preferredLocale
  );
  const oldParsedContextData = omit(parsedContext, ['operations', 'overrides']);
  const shouldUpdateContext = !isEqual(
    newParsedContextData,
    oldParsedContextData
  );

  let updatedParsedContext: UserContextProps;
  if (shouldUpdateContext) {
    updatedParsedContext = newParsedContextData;
  } else {
    updatedParsedContext = parsedContext;
  }

  useEffect(() => {
    if (shouldUpdateContext) {
      setParsedContext(updatedParsedContext);
    }
  }, [shouldUpdateContext, updatedParsedContext]);

  return updatedParsedContext;
}
